import React, {Fragment, useState} from 'react';
import {observer} from "mobx-react";
import HeightPin from "../HeightPin";
import ScrollSection from "../ScrollSection";
import GemDiscover from "../elements/GemDiscover";
import MoodBoard from "../MoodBoard"
import ImageCirclePack from "../charts/ImageCirclePack";
import TreeMap from "../charts/TreeMap";
import CardFlip from "../elements/CardFlip";
import DidYouKnow from "../elements/DidYouKnow";
import FormElement from "../elements/FormElement";
import Results from "../elements/Results";
import SectionTop from "./SectionTop";
import {getAltText, getSectionImage, getInfoClass, getImageInfoStyle} from "../../assets/settings";

function EnergyWaterClimate(props) {
    const {store, state, color, index, downhill} = props;
    const {circlePackStore} = store;

    const group = 'EWC';
    const activeKey = store.activeSection(group) || 'EWC_2';


    const sectionY = 0.35;

    return (
        <div className={'Section'}>
            <SectionTop group={group} downhill={downhill}>
                  <h4>
                    The campus has a complex network of infrastructure systems for energy, telecom, and water that supports the research, learning, and living that happens every day.
                  </h4>
                  <p>
                    The Long Range Development Plan and Campus Master Plan will consider opportunities to make these systems more resilient, as well as to reduce the campus’s environmental footprint. These plans can identify how physical improvements could support and exceed University of California and UC Berkeley sustainability goals.
                  </p>
                  <p className="photocredit">
                    Photo Credit: Keegan Houser
                  </p>
            </SectionTop>
            <HeightPin store={store} state={state} scrollChild={<div className={'left-scroll'}>
                <div className={'spacer'} style={{height: 50}}/>

                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`EWC_2`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'EWC_2')}>
                      <p>
                        <a target="_blank" href="https://ucop.edu/sustainability/policy-areas/index.html">The University of California Sustainable Practices Policy</a> establishes goals for:
                      </p>
                      <ul>
                        <li>Green building</li>
                        <li>Clean energy</li>
                        <li>Transportation</li>
                        <li>Climate protection</li>
                        <li>Sustainable operations</li>
                        <li>Waste reduction and recycling</li>
                        <li>Environmentally preferable purchasing</li>
                        <li>Sustainable food service</li>
                        <li>Sustainable water systems</li>
                      </ul>
                      <p className="photocredit">
                        Photo Credit: UC Berkeley
                      </p>
                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`EWC_3`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'EWC_3')}>
                      <h4>Sustainability and resilience are two separate but often overlapping goals.</h4>
                      <p>
                        Sustainability is about asking “How do we minimize environmental impacts?”
                      </p>
                      <p>
                        Sustainability strategies include reducing energy, water, and carbon emissions.
                      </p>
                      <p>
                        For example:
                      </p>
                      <ul>
                        <li>Replace fluorescent lighting with LEDs</li>
                        <li>Update buildings’ electrical systems</li>
                        <li>Convert natural gas to electricity</li>
                        <li>Generate energy on-site (e.g. solar panels)</li>
                        <li>Store electricity on-site to reduce emissions</li>
                        <li>Prioritize sustainable transportation strategies</li>
                        <li>Use low-flow water fixtures</li>
                        <li>Use stormwater management strategies like bioswales</li>
                      </ul>
                      <p className="photocredit">
                        Photo Credit: Elena Zhukova
                      </p>
                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`EWC_4`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'EWC_4')}>
                        <p>
                          Resilience is about asking “How do we adapt to current and future hazards and emergencies?”
                        </p>
                        <p>
                          Resilience strategies include planning for earthquakes, extreme weather events, poor air quality, and power shut-offs.
                        </p>
                        <p>
                          For example:
                        </p>
                        <ul>
                          <li>Improve building systems to support classrooms and libraries during smoke days and power shut-offs</li>
                          <li>Improve IT infrastructure to enable remote working when people are unable to get to campus</li>
                          <li>Generate and store electricity on-site</li>
                          <li>Diversify tree and plant species for resilience against pests, fire, and drought</li>
                        </ul>
                        <p className="photocredit">
                          Photo Credit: Keegan Houser
                        </p>
                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`EWC_5`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'EWC_5')}>
                      <h4>
                        What are the climate trends and natural hazards that will affect the campus?
                      </h4>
                      <p>Warmer Weather</p>
                      <ul>
                        <li>The number of hot days is projected to quadruple by 2090 (Source: Cal-Adapt; Canadian Earth System Model)</li>
                      </ul>
                      <p>Less Rainfall</p>
                      <ul>
                        <li>Expected 26” per year average annual precipitation, compared to 26.7” today (Source: U.S. Climate Data)</li>
                        <li>Climate projected to become drier with longer periods of drought</li>
                      </ul>

                      <p>Fire Hazards</p>
                      <ul>
                        <li>Hill Campus is located within a “Local Responsibility Area Very High Fire Hazard Severity Zone” (Source: CAL FIRE)</li>
                      </ul>

                      <p>Extreme Storms</p>
                      <ul>
                        <li>Storms are anticipated to become larger and more severe, e.g. large period of drought punctuated by short, intense storms that drop more rainfall in a shorter period of time</li>
                      </ul>

                      <p>Earthquakes</p>
                      <ul>
                        <li>Seismic safety will continue to be an important topic for the campus, as it has been since the 1970s</li>
                      </ul>
                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`EWC_6`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'EWC_6')}>
                      <h4>
                        The campus’s energy infrastructure consists of several interconnected systems.
                      </h4>
                      <h4>
                        UC Berkeley has committed to being carbon neutral by 2025, and reducing energy use is core to meeting this goal.
                      </h4>
                      <p>
                        How could the campus reduce energy use?
                      </p>
                      <ul>
                        <li>Measure energy use and identify large users</li>
                        <li>Prioritize buildings for energy efficiency upgrades</li>
                        <li>Replace or mitigate energy-intensive buildings</li>
                        <li>Design new buildings to be energy efficient and adaptable</li>
                        <li>Increase the campus’s capacity to store power on campus (e.g. solar panels, batteries)</li>
                        <li>Develop a microgrid for the campus (a power grid that can operate autonomously from regional power networks)</li>
                        <li>Invest in biogas (biofuel that is naturally produced from the decomposition of organic waste)</li>
                        <li>Purchase clean power</li>
                      </ul>
                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`EWC_7`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'EWC_7')}>
                      <h4>
                        How is water used on campus?
                      </h4>
                      <p>
                        Today, the majority of water consumption goes toward research and residence halls. Irrigation accounts for less than 10% of campus water usage, even though the landscape is a prominent feature of the campus.
                      </p>
                      <p>
                        Future climate trends and additional development will put pressure on existing systems due to increased winter precipitation, storm intensities, and impervious surfaces. Water consumption could be decreased or mitigated by:
                      </p>
                      <ul>
                        <li>More stormwater management</li>
                        <li>Water reuse strategies</li>
                        <li>More efficient fixtures in buildings</li>
                      </ul>
                      <DidYouKnow store={store} color={color}>
                          <p>Campus water consumption has decreased steadily since 2006 despite growth in campus population, due to infrastructure upgrades, improved metering, new water use policies, increased awareness of water scarcity, and low-irrigation landscapes.</p>
                      </DidYouKnow>
                    </div>
                </ScrollSection>

                <div className={'spacer'} style={{height: '50vh'}}/>
            </div>}
                       pinChild={<div className={'sticky-image-right'}>
                           <div className={'frame'}>
                              {getAltText(activeKey).title && <h4 style={{'backgroundColor': color + '80'}}>{getAltText(activeKey).title}</h4>}
                               <img width={'100%'} src={getSectionImage(group, activeKey)} alt={getAltText(activeKey).alt} />
                           </div>
                       </div>}
            />
            <div className={'interactive'}>
                <CardFlip color={color} index={index} store={store}
                        frontFace={<FormElement store={store} id={group} />}
                        backFace={<Fragment>
                            <Results store={store} id={group} />
                            {/*<GemDiscover store={store} color={color} index={index}/>*/}

                        </Fragment>}/>
            </div>
        </div>
    );
}

export default observer(EnergyWaterClimate);
